import { useState, useEffect } from 'react'
import {
  Users, FileText, Calendar, DollarSign, TrendingUp, Target, Clock,
  AlertTriangle, CheckCircle, ArrowRight, Activity, CreditCard
} from 'lucide-react'
import { useFilters } from '../contexts/FilterContext'
import { api, KPIsResponse, EvolucaoItem, CorretorRanking } from '../services/api'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend, PieChart, Pie, Cell
} from 'recharts'
import styles from './Overview.module.css'

interface LinhaPerformance {
  vendas: number
  vgv: number
  ticket_medio: number
  conversao: number
}

interface SLAData {
  sem_atendimento: number
  parados: number
  em_risco: number
  conformidade: number
  total_ativos: number
}

interface TeamData {
  leads: number
  vendas: number
  conversao: number
  vgv: number
}

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4']

export function Overview() {
  const { queryParams, filtersVersion } = useFilters()
  const [kpis, setKpis] = useState<KPIsResponse | null>(null)
  const [evolucao, setEvolucao] = useState<EvolucaoItem[]>([])
  const [topCorretores, setTopCorretores] = useState<CorretorRanking[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Performance por Linha
  const [investData, setInvestData] = useState<LinhaPerformance>({ vendas: 0, vgv: 0, ticket_medio: 0, conversao: 0 })
  const [realizaData, setRealizaData] = useState<LinhaPerformance>({ vendas: 0, vgv: 0, ticket_medio: 0, conversao: 0 })

  // SLA e Alertas
  const [slaData, setSlaData] = useState<SLAData>({ sem_atendimento: 0, parados: 0, em_risco: 0, conformidade: 0, total_ativos: 0 })

  // Times
  const [utaniData, setUtaniData] = useState<TeamData>({ leads: 0, vendas: 0, conversao: 0, vgv: 0 })
  const [alfaData, setAlfaData] = useState<TeamData>({ leads: 0, vendas: 0, conversao: 0, vgv: 0 })
  const [viabilizadoresData, setViabilizadoresData] = useState<TeamData>({ leads: 0, vendas: 0, conversao: 0, vgv: 0 })

  // Dados de temperatura e DDD
  const [temperatureData, setTemperatureData] = useState<any[]>([])
  const [dddData, setDddData] = useState<any[]>([])

  // Mídia Paga (CPL, CAC, ROI, Investimento)
  const [midiaPaga, setMidiaPaga] = useState<any>(null)

  // Tempos de Etapa
  const [temposEtapas, setTemposEtapas] = useState<any[]>([])

  // Volume por Situação (Etapas)
  const [situacaoData, setSituacaoData] = useState<any[]>([])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        // Carrega dados principais
        // Carrega TODOS os dados em paralelo para máxima performance
        const [
          kpisData,
          evolucaoData,
          corretoresData,
          alertasResponse,
          ddd,
          situacao,
          temp,
          midiaPagaData,
          tempos,
          linhasData,
          timesData,
        ] = await Promise.all([
          api.getKPIs(queryParams),
          api.getEvolucaoDiaria(queryParams),
          api.getCorretoresRanking(`${queryParams}&limit=5`),
          api.getAlertasSLA(queryParams).catch(() => null),
          api.getLeadsPorDDD(queryParams, 5).catch(() => null),
          api.getLeadsPorSituacao(queryParams).catch(() => null),
          api.getLeadsPorTemperatura(queryParams).catch(() => null),
          api.getMidiaPagaResumo(queryParams).catch(() => null),
          api.getLeadsTempoMedioEtapas(queryParams).catch(() => null),
          api.getLinhasNegocio(queryParams).catch(() => null),
          api.getTimes(queryParams).catch(() => null),
        ])

        setKpis(kpisData)
        setEvolucao(evolucaoData)
        setTopCorretores(corretoresData)

        // SLA
        if (alertasResponse?.alertas) {
          const alertasArray = alertasResponse.alertas
          const semContato = alertasArray.find((a: any) => a.label?.includes('sem 1'))
          const parados = alertasArray.find((a: any) => a.label?.includes('parados'))
          const emRisco = alertasArray.find((a: any) => a.label?.includes('risco'))
          setSlaData({
            sem_atendimento: semContato?.quantidade || 0,
            parados: parados?.quantidade || 0,
            em_risco: emRisco?.quantidade || 0,
            conformidade: alertasResponse.resumo?.taxa_conformidade || 0,
            total_ativos: alertasResponse.resumo?.total_leads_ativos || 0,
          })
        }

        // DDD
        if (ddd) {
          setDddData(ddd.map((item: any) => ({
            name: `DDD ${item.ddd}`,
            value: item.quantidade || 0,
          })))
        }

        // Situação
        if (situacao) {
          setSituacaoData(situacao.map((item: any) => ({
            name: item.situacao || 'Sem situação',
            quantidade: item.quantidade || 0,
            percentual: item.percentual || 0,
          })))
        }

        // Temperatura
        if (temp) {
          setTemperatureData(temp.map((item: any) => ({
            name: `Score ${item.score}`,
            value: item.quantidade || 0,
          })))
        }

        // Mídia Paga
        if (midiaPagaData) {
          setMidiaPaga(midiaPagaData)
        }

        // Tempos
        if (tempos) {
          setTemposEtapas(tempos)
        }

        // Linhas de Negócio
        if (linhasData) {
          setInvestData({
            vendas: linhasData.invest?.vendas || 0,
            vgv: linhasData.invest?.vgv || 0,
            ticket_medio: linhasData.invest?.ticket_medio || 0,
            conversao: linhasData.invest?.taxa_conversao || 0,
          })
          setRealizaData({
            vendas: linhasData.realiza?.vendas || 0,
            vgv: linhasData.realiza?.vgv || 0,
            ticket_medio: linhasData.realiza?.ticket_medio || 0,
            conversao: linhasData.realiza?.taxa_conversao || 0,
          })
        }

        // Times - internos = UTANI+SDR, parceiros = imobiliárias externas
        if (timesData) {
          // Busca dados individuais dos times internos via ranking
          const ranking = timesData.ranking || []
          const internos = timesData.internos?.times || []
          const utani = internos.find((t: any) => t.nome?.toUpperCase().includes('UTANI'))
          const sdr = internos.find((t: any) => t.nome === 'SDR' || t.nome?.toUpperCase().includes('ALFA'))

          setUtaniData({
            leads: utani?.leads || 0,
            vendas: utani?.vendas || 0,
            conversao: utani?.taxa_conversao || 0,
            vgv: utani?.vgv || 0,
          })
          setAlfaData({
            leads: sdr?.leads || 0,
            vendas: sdr?.vendas || 0,
            conversao: sdr?.taxa_conversao || 0,
            vgv: sdr?.vgv || 0,
          })
          // Viabilizadores = parceiros externos
          const parceiros = timesData.parceiros?.total || {}
          setViabilizadoresData({
            leads: parceiros.leads || 0,
            vendas: parceiros.vendas || 0,
            conversao: timesData.parceiros?.taxa_conversao || 0,
            vgv: parceiros.vgv || 0,
          })
        }

      } catch (err) {
        console.error('Erro ao carregar dados:', err)
        setError('Erro ao carregar dados do dashboard')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [queryParams, filtersVersion])

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`
    }
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(0)}K`
    }
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  // Calcula taxas de conversao
  const taxaLeadPre = kpis && kpis.total_leads > 0
    ? ((kpis.total_pre_cadastros / kpis.total_leads) * 100).toFixed(1)
    : '0.0'
  const taxaPreReserva = kpis && kpis.total_pre_cadastros > 0
    ? ((kpis.total_reservas / kpis.total_pre_cadastros) * 100).toFixed(1)
    : '0.0'
  const taxaReservaVenda = kpis && kpis.total_reservas > 0
    ? ((kpis.total_vendas / kpis.total_reservas) * 100).toFixed(1)
    : '0.0'

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Overview</h1>
            <p className={styles.pageSubtitle}>Visao geral do desempenho comercial</p>
          </div>
        </div>
        <div className={styles.errorState}>
          <p>{error}</p>
          <p>Verifique se a API esta funcionando corretamente.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Overview</h1>
          <p className={styles.pageSubtitle}>Visao geral do desempenho comercial - 29 KPIs monitorados</p>
        </div>
      </div>

      {/* 1. Captacao e Funil de Vendas */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>1. Captacao e Funil de Vendas</h2>
        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.primary}`}>
              <Users size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Leads Recebidos</span>
              <span className={styles.kpiValue}>{loading ? '-' : formatNumber(kpis?.total_leads || 0)}</span>
              {kpis?.variacao?.leads && (
                <div className={`${styles.kpiVariation} ${kpis.variacao.leads >= 0 ? styles.positive : styles.negative}`}>
                  <span>{kpis.variacao.leads >= 0 ? '+' : ''}{kpis.variacao.leads.toFixed(1)}% vs periodo anterior</span>
                </div>
              )}
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.secondary}`}>
              <FileText size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Pre-Cadastros Efetivados</span>
              <span className={styles.kpiValue}>{loading ? '-' : formatNumber(kpis?.total_pre_cadastros || 0)}</span>
              <div className={styles.kpiVariation}>
                <span>{taxaLeadPre}% dos leads</span>
              </div>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.warning}`}>
              <Calendar size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Reservas Realizadas</span>
              <span className={styles.kpiValue}>{loading ? '-' : formatNumber(kpis?.total_reservas || 0)}</span>
              <div className={styles.kpiVariation}>
                <span>{taxaPreReserva}% dos pre-cadastros</span>
              </div>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.success}`}>
              <DollarSign size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Vendas Fechadas</span>
              <span className={styles.kpiValue}>{loading ? '-' : formatNumber(kpis?.total_vendas || 0)}</span>
              {kpis?.variacao?.vendas && (
                <div className={`${styles.kpiVariation} ${kpis.variacao.vendas >= 0 ? styles.positive : styles.negative}`}>
                  <span>{kpis.variacao.vendas >= 0 ? '+' : ''}{kpis.variacao.vendas.toFixed(1)}% vs periodo anterior</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Performance por Linha de Negocio */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Performance por Linha de Negocio</h2>
        <div className={styles.linhaGrid}>
          {/* FAJ INVEST */}
          <div className={`${styles.linhaCard} ${styles.invest}`}>
            <div className={styles.linhaHeader}>
              <span className={`${styles.linhaBadge} ${styles.invest}`}>FAJ INVEST</span>
              <span className={styles.linhaSubtitle}>Smart Studios & Investimentos</span>
            </div>
            <div className={styles.linhaMetrics}>
              <div className={styles.linhaMetric}>
                <span className={styles.linhaMetricValue}>{loading ? '-' : formatNumber(investData.vendas)}</span>
                <span className={styles.linhaMetricLabel}>Vendas</span>
              </div>
              <div className={styles.linhaMetric}>
                <span className={styles.linhaMetricValue}>{loading ? '-' : formatCurrency(investData.vgv)}</span>
                <span className={styles.linhaMetricLabel}>VGV Vendido</span>
              </div>
              <div className={styles.linhaMetric}>
                <span className={styles.linhaMetricValue}>{loading ? '-' : formatCurrency(investData.ticket_medio)}</span>
                <span className={styles.linhaMetricLabel}>Ticket Medio</span>
              </div>
              <div className={styles.linhaMetric}>
                <span className={`${styles.linhaMetricValue} ${styles.success}`}>{investData.conversao.toFixed(1)}%</span>
                <span className={styles.linhaMetricLabel}>Conversao Lead→Venda</span>
              </div>
            </div>
            <div className={styles.linhaFooter}>
              <span>SOHO JK - SOHO Boa Vista - Vista9 - SOHO Square</span>
            </div>
          </div>

          {/* FAJ REALIZA */}
          <div className={`${styles.linhaCard} ${styles.realiza}`}>
            <div className={styles.linhaHeader}>
              <span className={`${styles.linhaBadge} ${styles.realiza}`}>FAJ REALIZA</span>
              <span className={styles.linhaSubtitle}>Minha Casa Minha Vida</span>
            </div>
            <div className={styles.linhaMetrics}>
              <div className={styles.linhaMetric}>
                <span className={styles.linhaMetricValue}>{loading ? '-' : formatNumber(realizaData.vendas)}</span>
                <span className={styles.linhaMetricLabel}>Vendas</span>
              </div>
              <div className={styles.linhaMetric}>
                <span className={styles.linhaMetricValue}>{loading ? '-' : formatCurrency(realizaData.vgv)}</span>
                <span className={styles.linhaMetricLabel}>VGV Vendido</span>
              </div>
              <div className={styles.linhaMetric}>
                <span className={styles.linhaMetricValue}>{loading ? '-' : formatCurrency(realizaData.ticket_medio)}</span>
                <span className={styles.linhaMetricLabel}>Ticket Medio</span>
              </div>
              <div className={styles.linhaMetric}>
                <span className={`${styles.linhaMetricValue} ${styles.success}`}>{realizaData.conversao.toFixed(1)}%</span>
                <span className={styles.linhaMetricLabel}>Conversao Lead→Venda</span>
              </div>
            </div>
            <div className={styles.linhaFooter}>
              <span>Marsala - Piavon - Carrara - Cremona - Vetorasso - +5</span>
            </div>
          </div>
        </div>
      </section>

      {/* Taxas de Conversao do Funil */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Taxas de Conversao do Funil</h2>
        <div className={styles.kpiGrid3}>
          <div className={`${styles.kpiCard} ${styles.conversionCard}`}>
            <div className={styles.conversionFlow}>
              <span>Lead</span>
              <ArrowRight size={20} />
              <span>Pre-Cadastro</span>
            </div>
            <span className={styles.conversionValue}>{taxaLeadPre}%</span>
            <span className={styles.kpiLabel}>{kpis?.total_pre_cadastros || 0} de {kpis?.total_leads || 0} leads</span>
          </div>

          <div className={`${styles.kpiCard} ${styles.conversionCard}`}>
            <div className={styles.conversionFlow}>
              <span>Pre-Cadastro</span>
              <ArrowRight size={20} />
              <span>Reserva</span>
            </div>
            <span className={styles.conversionValue}>{taxaPreReserva}%</span>
            <span className={styles.kpiLabel}>{kpis?.total_reservas || 0} de {kpis?.total_pre_cadastros || 0} pre-cadastros</span>
          </div>

          <div className={`${styles.kpiCard} ${styles.conversionCard}`}>
            <div className={styles.conversionFlow}>
              <span>Reserva</span>
              <ArrowRight size={20} />
              <span>Venda</span>
            </div>
            <span className={`${styles.conversionValue} ${styles.success}`}>{taxaReservaVenda}%</span>
            <span className={styles.kpiLabel}>{kpis?.total_vendas || 0} de {kpis?.total_reservas || 0} reservas</span>
          </div>
        </div>
      </section>

      {/* Tempo Medio entre Etapas */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Tempo Medio entre Etapas do Funil</h2>
        <div className={styles.kpiGrid4}>
          <div className={`${styles.kpiCard} ${styles.kpiCardSm}`}>
            <div className={`${styles.kpiIconSm} ${styles.primary}`}>
              <Clock size={16} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Lead → Pre-Cadastro</span>
              <span className={styles.kpiValueSm}>
                {loading ? '-' : `${temposEtapas?.etapas?.[0]?.tempo_medio_dias || 0} dias`}
              </span>
            </div>
          </div>
          <div className={`${styles.kpiCard} ${styles.kpiCardSm}`}>
            <div className={`${styles.kpiIconSm} ${styles.secondary}`}>
              <Clock size={16} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Pre-Cadastro → Reserva</span>
              <span className={styles.kpiValueSm}>
                {loading ? '-' : `${temposEtapas?.etapas?.[1]?.tempo_medio_dias || 0} dias`}
              </span>
            </div>
          </div>
          <div className={`${styles.kpiCard} ${styles.kpiCardSm}`}>
            <div className={`${styles.kpiIconSm} ${styles.success}`}>
              <Clock size={16} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Lead → Venda (Total)</span>
              <span className={styles.kpiValueSm}>
                {loading ? '-' : `${temposEtapas?.etapas?.[2]?.tempo_medio_dias || 0} dias`}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Custos e Performance de Marketing */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>2. Custos e Performance de Marketing</h2>
        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.info}`}>
              <DollarSign size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>CPL (Custo por Lead)</span>
              <span className={styles.kpiValue}>
                {loading ? '-' : formatCurrency(midiaPaga?.metricas?.cpl || 0)}
              </span>
              <div className={styles.kpiVariation}>
                <span>{midiaPaga?.resultados?.total_leads || 0} leads captados</span>
              </div>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.warning}`}>
              <CreditCard size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>CAC (Custo por Cliente)</span>
              <span className={styles.kpiValue}>
                {loading ? '-' : formatCurrency(midiaPaga?.metricas?.cac || 0)}
              </span>
              <div className={styles.kpiVariation}>
                <span>{midiaPaga?.resultados?.total_vendas || 0} vendas realizadas</span>
              </div>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.success}`}>
              <TrendingUp size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>ROI (Retorno Investimento)</span>
              <span className={styles.kpiValue}>
                {loading ? '-' : `${((midiaPaga?.metricas?.roi || 0) / 100).toFixed(1)}x`}
              </span>
              <div className={`${styles.kpiVariation} ${(midiaPaga?.metricas?.roi || 0) > 100 ? styles.positive : styles.negative}`}>
                <span>{(midiaPaga?.metricas?.roi || 0) > 100 ? 'Retorno positivo' : 'Abaixo do esperado'}</span>
              </div>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.primary}`}>
              <Target size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Investimento Total</span>
              <span className={styles.kpiValue}>
                {loading ? '-' : formatCurrency(midiaPaga?.investimento?.total || 0)}
              </span>
              <div className={styles.kpiVariation}>
                <span>Periodo selecionado</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Perfil e Qualificacao de Leads */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>3. Perfil e Qualificacao de Leads</h2>
        <div className={styles.chartsGrid3}>
          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Leads por Temperatura (Score)</h3>
              <p className={styles.chartSubtitle}>Qualificacao de 0 (frio) a 5 (quente)</p>
            </div>
            <div className={styles.chartContainer}>
              {temperatureData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={temperatureData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={70} fontSize={12} />
                      <Tooltip />
                      <Bar dataKey="value" name="Leads" radius={[0, 4, 4, 0]}>
                        {temperatureData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              entry.name.includes('0') || entry.name.includes('1') ? '#3b82f6' :
                              entry.name.includes('2') || entry.name.includes('3') ? '#f59e0b' :
                              '#ef4444'
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className={styles.temperatureLegend}>
                    <div className={styles.legendItem}>
                      <span className={styles.legendColor} style={{ backgroundColor: '#3b82f6' }}></span>
                      <span>Frio (0-1)</span>
                    </div>
                    <div className={styles.legendItem}>
                      <span className={styles.legendColor} style={{ backgroundColor: '#f59e0b' }}></span>
                      <span>Morno (2-3)</span>
                    </div>
                    <div className={styles.legendItem}>
                      <span className={styles.legendColor} style={{ backgroundColor: '#ef4444' }}></span>
                      <span>Quente (4-5)</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.noData}>Dados nao disponiveis</div>
              )}
            </div>
          </div>

          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Leads por Regiao (DDD)</h3>
              <p className={styles.chartSubtitle}>Distribuicao geografica</p>
            </div>
            <div className={styles.chartContainer}>
              {dddData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={dddData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={60} fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.noData}>Dados nao disponiveis</div>
              )}
            </div>
          </div>

          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Top 5 Corretores</h3>
              <p className={styles.chartSubtitle}>Por numero de vendas</p>
            </div>
            <div className={styles.rankingList}>
              {loading ? (
                <div className={styles.noData}>Carregando...</div>
              ) : topCorretores.length === 0 ? (
                <div className={styles.noData}>Nenhum corretor encontrado</div>
              ) : (
                topCorretores.map((corretor) => (
                  <div key={corretor.id} className={styles.rankingItem}>
                    <span className={styles.rankingPosition}>{corretor.posicao}</span>
                    <span className={styles.rankingName}>{corretor.nome}</span>
                    <span className={styles.rankingValue}>{corretor.total_vendas} vendas</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Alertas e SLA */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>4. Alertas e SLA</h2>
        <div className={styles.kpiGrid}>
          <div className={`${styles.kpiCard} ${styles.alertCard}`}>
            <div className={`${styles.kpiIcon} ${styles.danger}`}>
              <AlertTriangle size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Leads sem 1o contato (+24h)</span>
              <span className={`${styles.kpiValue} ${styles.dangerText}`}>{slaData.sem_atendimento}</span>
              <div className={`${styles.kpiVariation} ${styles.negative}`}>
                <span>Requer acao imediata</span>
              </div>
            </div>
          </div>

          <div className={`${styles.kpiCard} ${styles.alertCard}`}>
            <div className={`${styles.kpiIcon} ${styles.warning}`}>
              <Clock size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Leads parados (+7 dias)</span>
              <span className={`${styles.kpiValue} ${styles.warningText}`}>{slaData.parados}</span>
              <div className={`${styles.kpiVariation} ${styles.negative}`}>
                <span>Leads esfriando</span>
              </div>
            </div>
          </div>

          <div className={`${styles.kpiCard} ${styles.alertCard}`}>
            <div className={`${styles.kpiIcon} ${styles.warning}`}>
              <Activity size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Em Risco (5-7 dias)</span>
              <span className={`${styles.kpiValue} ${styles.warningText}`}>{slaData.em_risco}</span>
              <div className={`${styles.kpiVariation} ${styles.negative}`}>
                <span>Proximos de violar SLA</span>
              </div>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.success}`}>
              <CheckCircle size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Conformidade SLA</span>
              <span className={`${styles.kpiValue} ${styles.successText}`}>{slaData.conformidade.toFixed(1)}%</span>
              <div className={`${styles.kpiVariation} ${styles.positive}`}>
                <span>{slaData.total_ativos} leads ativos</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparacao entre Times */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>5. Comparacao entre Times</h2>
        <div className={styles.teamsGrid}>
          <div className={styles.teamCard}>
            <div className={styles.teamHeader}>
              <h3 className={styles.teamName}>Time UTANI</h3>
              <span className={`${styles.teamBadge} ${styles.house}`}>Interno</span>
            </div>
            <div className={styles.teamMetrics}>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>Leads</span>
                <span className={styles.teamMetricValue}>{formatNumber(utaniData.leads)}</span>
              </div>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>Vendas</span>
                <span className={styles.teamMetricValue}>{formatNumber(utaniData.vendas)}</span>
              </div>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>Conversao</span>
                <span className={`${styles.teamMetricValue} ${styles.successText}`}>{utaniData.conversao.toFixed(1)}%</span>
              </div>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>VGV</span>
                <span className={styles.teamMetricValue}>{formatCurrency(utaniData.vgv)}</span>
              </div>
            </div>
          </div>

          <div className={styles.teamCard}>
            <div className={styles.teamHeader}>
              <h3 className={styles.teamName}>Time SDR</h3>
              <span className={`${styles.teamBadge} ${styles.alpha}`}>Interno</span>
            </div>
            <div className={styles.teamMetrics}>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>Leads</span>
                <span className={styles.teamMetricValue}>{formatNumber(alfaData.leads)}</span>
              </div>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>Vendas</span>
                <span className={styles.teamMetricValue}>{formatNumber(alfaData.vendas)}</span>
              </div>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>Conversao</span>
                <span className={`${styles.teamMetricValue} ${styles.successText}`}>{alfaData.conversao.toFixed(1)}%</span>
              </div>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>VGV</span>
                <span className={styles.teamMetricValue}>{formatCurrency(alfaData.vgv)}</span>
              </div>
            </div>
          </div>

          <div className={styles.teamCard}>
            <div className={styles.teamHeader}>
              <h3 className={styles.teamName}>Viabilizadores</h3>
              <span className={`${styles.teamBadge} ${styles.parcerias}`}>Externo</span>
            </div>
            <div className={styles.teamMetrics}>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>Leads</span>
                <span className={styles.teamMetricValue}>{formatNumber(viabilizadoresData.leads)}</span>
              </div>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>Vendas</span>
                <span className={styles.teamMetricValue}>{formatNumber(viabilizadoresData.vendas)}</span>
              </div>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>Conversao</span>
                <span className={`${styles.teamMetricValue} ${styles.successText}`}>{viabilizadoresData.conversao.toFixed(1)}%</span>
              </div>
              <div className={styles.teamMetric}>
                <span className={styles.teamMetricLabel}>VGV</span>
                <span className={styles.teamMetricValue}>{formatCurrency(viabilizadoresData.vgv)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Graficos de Evolucao */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>6. Analise Comparativa</h2>
        <div className={styles.chartsGrid}>
          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Evolucao do Funil</h3>
              <p className={styles.chartSubtitle}>Leads e vendas por dia</p>
            </div>
            {loading ? (
              <div className={styles.chartPlaceholder}>
                <p>Carregando...</p>
              </div>
            ) : (
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={evolucao}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="data" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="leads" stroke="#3b82f6" strokeWidth={2} name="Leads" dot={false} />
                    <Line type="monotone" dataKey="vendas" stroke="#10b981" strokeWidth={2} name="Vendas" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Volume por Etapa</h3>
              <p className={styles.chartSubtitle}>Distribuicao de leads por situacao</p>
            </div>
            {loading ? (
              <div className={styles.chartPlaceholder}>
                <p>Carregando...</p>
              </div>
            ) : situacaoData.length > 0 ? (
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={situacaoData} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" stroke="#6b7280" fontSize={12} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      stroke="#6b7280"
                      fontSize={11}
                      width={180}
                      tickFormatter={(value) => value.length > 25 ? value.substring(0, 25) + '...' : value}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                      }}
                      formatter={(value: number, name: string) => [value.toLocaleString('pt-BR'), 'Quantidade']}
                    />
                    <Bar dataKey="quantidade" fill="#3b82f6" name="Leads" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className={styles.chartPlaceholder}>
                <p>Sem dados de situacao</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Metricas Gerais */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>7. Metricas Gerais</h2>
        <div className={styles.chartsGrid}>
          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Taxa de Conversao Geral</h3>
            </div>
            <div className={styles.bigMetric}>
              <span className={styles.bigMetricValue}>
                {loading ? '-' : `${kpis?.taxa_conversao?.toFixed(1) || 0}%`}
              </span>
              <span className={styles.bigMetricLabel}>Lead para Venda</span>
            </div>
            <div className={styles.metricsRow}>
              <div className={styles.metricItem}>
                <Target size={16} />
                <span>Ticket Medio:</span>
                <strong>{loading ? '-' : formatCurrency(kpis?.ticket_medio || 0)}</strong>
              </div>
              <div className={styles.metricItem}>
                <TrendingUp size={16} />
                <span>Leads Ativos:</span>
                <strong>{loading ? '-' : formatNumber(kpis?.leads_ativos || 0)}</strong>
              </div>
            </div>
          </div>

          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>VGV Total</h3>
            </div>
            <div className={styles.bigMetric}>
              <span className={styles.bigMetricValue}>
                {loading ? '-' : formatCurrency(kpis?.vgv || 0)}
              </span>
              <span className={styles.bigMetricLabel}>Volume Geral de Vendas</span>
            </div>
            <div className={styles.metricsRow}>
              <div className={styles.metricItem}>
                <DollarSign size={16} />
                <span>FAJ INVEST:</span>
                <strong>{formatCurrency(investData.vgv)}</strong>
              </div>
              <div className={styles.metricItem}>
                <DollarSign size={16} />
                <span>FAJ REALIZA:</span>
                <strong>{formatCurrency(realizaData.vgv)}</strong>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
